import moment from 'moment'
import get from 'lodash/get'
import * as Sentry from '@sentry/browser'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'
// Requests
import { QUESTION_YLOPO_OPT_IN } from '../constants/client/answers'
import { saveClientAnswer } from './client'
import { getListings, getListing } from '../api/nestready/requests'
import {
  LISTING_MARKERS_SEARCH,
  LISTINGS_SEARCH,
  listingsSearchCriteria,
} from '../constants/listings'
import { constructFilters } from '../helpers/listings/filters'
import { snakeCaseKeys } from '../helpers/utils'
import {
  TOGGLE_YLOPO_OPT_IN_MODAL,
  APPEND_LISTINGS,
  SET_YLOPO_LISTINGS,
  SET_SEARCH_LISTINGS_TOTAL,
  SET_SELECTED_LISTING_ID,
  SET_PREVIOUSLY_VIEWED_LISTING_ID,
  SET_LISTING_SOURCE,
  TOGGLE_LISTING_SEARCH_MODAL,
  FETCH_HOMEBOT_LISTINGS_REQUEST,
  FETCH_HOMEBOT_LISTINGS_SUCCESS,
  FETCH_HOMEBOT_LISTINGS_FAILURE,
} from './actionTypes'
import { CANCEL_TOKEN_MESSAGE } from '../api/nestready/requests/listings'

export const answerYlopoOptIn = () => async dispatch => {
  dispatch(
    saveClientAnswer({
      question: QUESTION_YLOPO_OPT_IN,
      response: {
        value: moment().toJSON(),
      },
    })
  )
}

export const toggleYlopoOptInModal = ylopoUrl => ({
  type: TOGGLE_YLOPO_OPT_IN_MODAL,
  ylopoUrl,
})

export const searchYlopoListings = (listings, tag) => ({
  type: SET_YLOPO_LISTINGS,
  listings,
  tag,
})

export const setSearchListingsTotal = total => ({
  type: SET_SEARCH_LISTINGS_TOTAL,
  total,
})

export const setSelectedListingId = id => ({
  type: SET_SELECTED_LISTING_ID,
  id,
})

export const setPreviouslyViewedListingId = id => ({
  type: SET_PREVIOUSLY_VIEWED_LISTING_ID,
  id,
})

export const appendListings = (listings, tag) => ({
  type: APPEND_LISTINGS,
  listings,
  tag,
})

export const setListingsSource = source => ({
  type: SET_LISTING_SOURCE,
  listingsSource: source,
})

export const toggleListingSearchModal = bool => ({
  type: TOGGLE_LISTING_SEARCH_MODAL,
  showSearchModal: bool,
})

const buildPhotoUrl = fileName => {
  if (fileName == null) {
    return
  }

  // For some reason, dev NR returns file path like /uploads/development/img2.jpg
  if (process.env.NODE_ENV === 'development') {
    return `${process.env.REACT_APP_NESTREADY_API_URL}${fileName}`
  }

  // While bev and prod envs return absolute urls to S3 buckets like https://bucket.s3.amazonaws.com/listing-images/img2.jpg
  return fileName
}

// fetches listings to show in the carousels
// on the MarketSimilarHomes, PurchasingPower, and EquitySmartSearch
// pages
export const searchListingsForCarousel = (customerId, params) => dispatch => {
  const { tag } = params

  dispatch({
    type: FETCH_HOMEBOT_LISTINGS_REQUEST,
    page: 1,
    tag,
  })

  getListings(customerId, params)
    .then(resp => {
      const listings = get(resp, 'data.listings', [])
      const transformedListings = listings.map(listing => {
        const {
          id,
          bedroomsCount,
          bathroomsCount,
          priceFormatted,
          priceCents,
          sqftTotal,
          thumbnailImageFileNames,
        } = listing

        return {
          id,
          bedrooms: bedroomsCount,
          bathrooms: bathroomsCount,
          formattedPrice: priceFormatted,
          price: priceCents / 100,
          livingAreaSqFt: sqftTotal,
          mainPhoto: buildPhotoUrl(thumbnailImageFileNames[0]),
          searchParams: params,
        }
      })

      dispatch({
        type: FETCH_HOMEBOT_LISTINGS_SUCCESS,
        page: 1,
        listings: transformedListings,
        tag,
      })
    })
    .catch(error => {
      dispatch({ type: FETCH_HOMEBOT_LISTINGS_FAILURE })
      Sentry.captureException(error)
    })
}

const buildListingsSearchParams = ({ perPage, page, ...params }) => {
  const search = {}

  if (params.boundingBox && Object.keys(params.boundingBox).length) {
    search.bounding_box = params.boundingBox
  } else {
    const { location } = params
    search.bounding_box = location.boundingBox
    search.route = location.stateAbbreviation
    search[`${location.level}_id`] = location.id
  }
  search.per_page = perPage
  search.page = page || 1
  search.compact = params.compact
  search.order = params.order
  search.filters = snakeCaseKeys(constructFilters(params))

  return search
}

export const searchListingsForMapMarkers = (customerId, params) => dispatch => {
  const tag = LISTING_MARKERS_SEARCH
  const search = buildListingsSearchParams({
    ...params,
    compact: true,
    page: 1,
    perPage: listingsSearchCriteria.MAX_NUMBER_OF_LISTING_MARKERS_FETCHED,
  })

  dispatch({
    type: FETCH_HOMEBOT_LISTINGS_REQUEST,
    page: search.page,
    tag,
  })

  return getListings(customerId, omitBy(search, isNil), 'Markers')
    .then(resp => {
      const listings = get(resp, 'data.listings', [])
      const total = get(resp, 'data.totalCount')

      dispatch(setSearchListingsTotal(total))

      // Need to return the promise so caller can wait for the finished request
      return dispatch({
        type: FETCH_HOMEBOT_LISTINGS_SUCCESS,
        page: search.page,
        listings,
        tag,
      })
    })
    .catch(error => {
      // if request is cancelled because the request is being fired too rapidly, we need to allow loading state to keep showing, and don't log error to sentry
      if (error?.message === CANCEL_TOKEN_MESSAGE) {
        return
      }
      dispatch({
        type: FETCH_HOMEBOT_LISTINGS_FAILURE,
        tag,
      })

      Sentry.captureException(error)
    })
}

export const searchListingsForListView = (customerId, params) => dispatch => {
  const tag = LISTINGS_SEARCH
  const search = buildListingsSearchParams({
    ...params,
    perPage: listingsSearchCriteria.SEARCH_RESULTS_PAGE_LIMIT,
  })

  dispatch({
    type: FETCH_HOMEBOT_LISTINGS_REQUEST,
    page: search.page,
    tag,
  })

  return getListings(customerId, omitBy(search, isNil), 'ListView')
    .then(resp => {
      const listings = get(resp, 'data.listings', [])
      const total = get(resp, 'data.totalCount')

      dispatch(setSearchListingsTotal(total))

      // Need to return the promise so caller can wait for the finished request
      return dispatch({
        type: FETCH_HOMEBOT_LISTINGS_SUCCESS,
        page: search.page,
        listings,
        tag,
      })
    })
    .catch(error => {
      // if request is cancelled because the request is being fired too rapidly, we need to allow loading state to keep showing, and don't log error to sentry
      if (error?.message === CANCEL_TOKEN_MESSAGE) {
        return
      }
      dispatch({
        type: FETCH_HOMEBOT_LISTINGS_FAILURE,
        tag,
      })

      Sentry.captureException(error)
    })
}

// Fetch additional listing from searchResults. Used when a marker is selected for a listing
// that is not yet in the listings results
export const fetchSearchListing = (customerId, listingId, tag) => dispatch => {
  const listingRequestData = {
    listingId: listingId,
    compact: true,
    customerId: customerId,
  }

  return new Promise(function (resolve, reject) {
    getListing(listingRequestData)
      .then(resp => {
        const listing = get(resp, 'data.data', [])
        dispatch(appendListings([listing], tag))
        resolve(listing)
      })
      .catch(error => {
        reject(error)
        Sentry.captureException(error)
      })
  })
}
